import { api } from './api';
import type { IgConnection, IgPublishLog } from '../types';

export const igPublishingApi = {
  startOAuth: (brandId?: number, channelId?: number, forceNew?: boolean) => {
    const qs = new URLSearchParams();
    if (brandId != null) qs.set('brand_id', String(brandId));
    if (channelId != null) qs.set('channel_id', String(channelId));
    if (forceNew) qs.set('force_new', 'true');
    const q = qs.toString();
    return api.get<{ auth_url: string }>(`/ig-publishing/oauth/start${q ? `?${q}` : ''}`);
  },

  getConnections: () => api.get<IgConnection[]>('/ig-publishing/connections'),

  disconnect: (id: number) => api.delete<{ ok: boolean }>(`/ig-publishing/connections/${id}`),

  getConnectionHealth: (id: number) =>
    api.get<{ status: string; expires_at: string; days_remaining: number }>(
      `/ig-publishing/connections/${id}/health`
    ),

  publish: (data: { channel_id: number; generation_id: number; post_type: string; scheduled_at?: string | null }) =>
    api.post<{ log_id: number; status: string }>('/ig-publishing/publish', data),

  // Link an existing global IG connection to a brand channel without a new
  // OAuth round. Backend rejects with 403 if the connection doesn't belong to
  // the current user (admins exempt).
  linkExistingConnection: (channelId: number, connectionId: number) =>
    api.post<{ channel_id: number; ig_connection_id: number }>(
      `/ig-publishing/channels/${channelId}/link-connection`,
      { connection_id: connectionId },
    ),

  getPublishHistory: (params?: { connection_id?: number; channel_id?: number; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.connection_id != null) qs.set('connection_id', String(params.connection_id));
    if (params?.channel_id != null) qs.set('channel_id', String(params.channel_id));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return api.get<{ items: IgPublishLog[]; total: number }>(
      `/ig-publishing/publish-history${q ? `?${q}` : ''}`
    );
  },

  getPublishStatus: (genId: number) =>
    api.get<IgPublishLog[]>(`/ig-publishing/publish-status/${genId}`),

  uploadImage: (base64: string, genId: number) =>
    api.post<{ url: string }>('/ig-publishing/upload-image', {
      generation_id: genId,
      base64,
    }),

  testPublish: (channelId: number, generationId: number, postType: string) =>
    api.post<{
      success: boolean;
      steps: Array<{ step: string; ok: boolean; error?: string; username?: string; container_id?: string; status?: string; image_url?: string }>;
      message: string;
    }>('/ig-publishing/test-publish', { channel_id: channelId, generation_id: generationId, post_type: postType }),

  runScheduler: () =>
    api.post<{ ran_at: string; posts_processed: number; results: Array<{ log_id: number; status: string; permalink?: string; error?: string }> }>(
      '/ig-publishing/run-scheduler',
      {}
    ),

  publishNow: (logId: number) =>
    api.post<{ log_id: number; status: string; channel_id: number }>(
      `/ig-publishing/publish-now/${logId}`,
      {}
    ),

  getSchedulerHealth: () =>
    api.get<{
      last_heartbeat: string | null;
      seconds_since_last_run: number | null;
      scheduler_ok: boolean;
      overdue_posts: number;
      upcoming_posts: number;
      server_time_utc: string;
    }>('/ig-publishing/scheduler-health'),
};
